/**
 * Auth telemetry helper — `auth_attempt_id` lifecycle + watchdog.
 *
 * Backbone of the 12-event auth diagnostic funnel (CRE-781). Every event in
 * the OAuth popup flow carries an `auth_attempt_id` ULID; the same ID is
 * threaded through the OAuth state cookie/JSON (backend half — CRE-782) so
 * the funnel can stitch client → popup → backend → parent into a single
 * trace, even when individual links silently break (COOP-severed opener,
 * dropped postMessage, origin drift).
 *
 * All public functions are sync + non-throwing. Persistence is
 * `sessionStorage` only — the attempt is intentionally scoped to the
 * popup-flow lifetime; nothing persists across browser sessions.
 *
 * Stages (recorded in sessionStorage as we observe them):
 *   - 'initiated'        — openPopup fired (event 1)
 *   - 'callback_landed'  — callback page mounted (event 3)
 *   - 'result_posted'    — popup sent its result (event 5)
 *   - 'result_received'  — parent received any result (event 6)
 *   - 'closed_no_result' — popup closed while waiting, no result (event 8)
 *   - 'synced'           — handleSuccess completed (event 9 success)
 *   - 'sync_failed'      — refetch returned anonymous (event 9 failure)
 *   - 'abandoned'        — watchdog fired (event 10)
 *
 * Watchdog: see `installAuthAbandonWatchdog` — runs on next pageview/
 * interaction after a flow has been in a non-terminal stage for >90s.
 */

import { LLM_SOURCES } from '@repo/analytics';
import { isEventPlatform, type EventPlatform } from '@repo/user-events';

import { env } from '@env';

const STORAGE_KEY = 'skim_auth_attempt';
const ABANDON_THRESHOLD_MS = 90_000;

// Crockford Base32 alphabet (excludes I, L, O, U to avoid ambiguity).
const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ULID_TIMESTAMP_LEN = 10; // 48 bits
const ULID_RANDOM_LEN = 16; // 80 bits

export type AuthFlow = 'popup' | 'redirect' | 'iframe_popup' | 'native';

export type AuthStage =
  | 'initiated'
  | 'callback_landed'
  | 'result_posted'
  | 'result_received'
  | 'closed_no_result'
  | 'synced'
  | 'sync_failed'
  | 'abandoned';

export interface AuthAttemptRecord {
  /** ULID (26 chars Crockford Base32) — the correlation backbone. */
  id: string;
  /** Provider picked at openPopup time. Null when unknown (iframe). */
  provider: string | null;
  /** Surface label (login wall, sidebar, etc.). */
  surface: string;
  /** Flow shape. */
  flow: AuthFlow;
  /** Epoch ms when openPopup ran (or equivalent for redirect/native). */
  t0: number;
  /** Latest known stage. */
  stage: AuthStage;
  /**
   * Which CLIENT this sign-in belongs to, for the first-party `UserEvent`
   * record. Carried here because this record is the only store that survives
   * the cross-origin OAuth redirect dance within the popup tab — the same
   * reason `id` lives here.
   *
   * An extension sign-in physically happens in a website popup, so by the time
   * the callback page runs, nothing in its own URL or origin distinguishes
   * "on skim.plus" from "in the browser extension". The originating surface
   * tags the popup URL with `?pf=extension` and `/sign-in` stores the resolved
   * platform here. Absent (older records, direct website visits) → the callback
   * defaults to WEBSITE.
   */
  platform?: EventPlatform;
}

/**
 * Generate a Crockford Base32 ULID — 10 timestamp chars + 16 random chars,
 * total 26 chars. Spec-compliant for the regex `^[0-9A-HJKMNP-TV-Z]{26}$`
 * that the backend allowlist uses for cookie/state validation.
 *
 * We use a minimal inline implementation rather than pulling `ulid` as a
 * dependency because this is the only place we need it and the algorithm
 * is short. `crypto.getRandomValues` is available in every browser we
 * support; falls back to `Math.random` only if the API is somehow missing
 * (and logs a warning so we'd notice).
 */
export const generateAuthAttemptId = (): string => {
  let timestamp = Date.now();
  const timeChars: string[] = [];
  for (let i = ULID_TIMESTAMP_LEN - 1; i >= 0; i -= 1) {
    timeChars[i] = ULID_ALPHABET[timestamp % 32]!;
    timestamp = Math.floor(timestamp / 32);
  }

  const randomBytes = new Uint8Array(ULID_RANDOM_LEN);
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.crypto?.getRandomValues === 'function'
    ) {
      window.crypto.getRandomValues(randomBytes);
    } else {
      throw new Error('crypto.getRandomValues unavailable');
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      '[auth-telemetry] crypto.getRandomValues unavailable — falling back to Math.random',
    );
    for (let i = 0; i < ULID_RANDOM_LEN; i += 1) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const randomChars: string[] = [];
  for (let i = 0; i < ULID_RANDOM_LEN; i += 1) {
    randomChars[i] = ULID_ALPHABET[randomBytes[i]! % 32]!;
  }

  return timeChars.join('') + randomChars.join('');
};

const safeSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    // Some embedded contexts throw on access (e.g. cross-origin iframes,
    // privacy modes). Telemetry is best-effort — return null and let
    // callers no-op gracefully.
    return null;
  }
};

/**
 * Write a new auth attempt record to sessionStorage and return it. Replaces
 * any prior attempt (overlapping attempts shouldn't happen in practice
 * because the popup is single-instance via `SkimOAuthSignIn` window name).
 *
 * `id` (optional): adopt an attempt id minted by an upstream surface
 * instead of generating a fresh one. Used by the /sign-in page when opened
 * as the landing-iframe's popup (`?aa=<ulid>`): adopting the iframe's id
 * keeps iframe → popup → backend → callback events stitched on ONE id,
 * and lets the iframe's rescue gate verify the resulting session against
 * the id it minted (CRE-783). Invalid/absent → mint fresh as before.
 */
export const startAuthAttempt = (params: {
  provider: string | null;
  surface: string;
  flow: AuthFlow;
  id?: string | null;
  /**
   * Client this sign-in belongs to (CRE user-events). Only `/sign-in` passes
   * it, resolved from the `?pf=` hint the landing iframe / extension append.
   * Omitted everywhere else — the callback then defaults to WEBSITE.
   */
  platform?: EventPlatform;
}): AuthAttemptRecord => {
  const record: AuthAttemptRecord = {
    id:
      params.id && /^[0-9A-HJKMNP-TV-Z]{26}$/.test(params.id)
        ? params.id
        : generateAuthAttemptId(),
    provider: params.provider,
    surface: params.surface,
    flow: params.flow,
    t0: Date.now(),
    stage: 'initiated',
    ...(params.platform ? { platform: params.platform } : {}),
  };
  const ss = safeSessionStorage();
  if (ss) {
    try {
      ss.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Quota / privacy mode — ignore.
    }
  }
  return record;
};

/**
 * Read the current attempt record. Returns null if none exists, the JSON
 * is corrupt, or sessionStorage is unavailable.
 */
export const getCurrentAuthAttempt = (): AuthAttemptRecord | null => {
  const ss = safeSessionStorage();
  if (!ss) return null;
  try {
    const raw = ss.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthAttemptRecord>;
    // Defensive: every field present + ID format sane.
    if (
      typeof parsed.id === 'string' &&
      /^[0-9A-HJKMNP-TV-Z]{26}$/.test(parsed.id) &&
      typeof parsed.surface === 'string' &&
      typeof parsed.flow === 'string' &&
      typeof parsed.t0 === 'number' &&
      typeof parsed.stage === 'string'
    ) {
      // `platform` is re-validated rather than trusted: the blob is
      // user-writable storage, and the value ends up on a first-party DB row.
      // An unrecognised value is dropped (→ the caller's WEBSITE default)
      // instead of being propagated.
      const record = parsed as AuthAttemptRecord;
      if (record.platform !== undefined && !isEventPlatform(record.platform)) {
        delete record.platform;
      }
      return record;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Update the current attempt's `stage` (and optionally `provider`). No-op
 * if no attempt exists. Used by event-firing sites to keep the watchdog's
 * abandonment view consistent with the actual flow progress.
 */
export const updateAuthAttemptStage = (
  stage: AuthStage,
  patch?: Partial<Pick<AuthAttemptRecord, 'provider'>>,
): void => {
  const ss = safeSessionStorage();
  if (!ss) return;
  const current = getCurrentAuthAttempt();
  if (!current) return;
  const next: AuthAttemptRecord = {
    ...current,
    ...patch,
    stage,
  };
  try {
    ss.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
};

/**
 * Clear the current attempt record. Called from terminal-state code paths
 * (synced, abandoned, error) so the watchdog can't double-fire.
 */
export const clearAuthAttempt = (): void => {
  const ss = safeSessionStorage();
  if (!ss) return;
  try {
    ss.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Append `aa=<id>` to an OAuth init URL. Backend reads `aa` from the
 * callback's query / state (see CRE-782) and threads it into the server-
 * side `oauth_callback_received` + `session_issued` events. Safe to call
 * with already-parameterized URLs (`?` vs `&` handled).
 */
export const appendAuthAttemptId = (url: string, attemptId: string): string => {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}aa=${encodeURIComponent(attemptId)}`;
};

// ---------------------------------------------------------------------------
// Acquisition params (CRE-854): the web SENDER half of the attribution chain.
//
// Today the OAuth-init URL carries only `redirect_to` + `aa`. The server-truth
// `signup_completed` event therefore has `surface` null in prod, and no
// acquisition origin (channel / referrer / utm) reaches the server event or
// the User row at all. This block puts those values ON THE WIRE so the backend
// receiver (CRE-856) can thread them onto `signup_completed` and persist them.
//
// Wire contract (snake_case KEYS. camelCase silently becomes NULL in the
// PostHog pipeline and would not match the backend `req.query` reads):
//   surface           the in-app entry label (backend reads req.query.surface)
//   acq_channel        acquisition channel from the locked taxonomy below
//   acq_referrer       bare referring host, or `$direct`
//   acq_utm_source     utm_source at landing
//   acq_utm_medium     utm_medium at landing
//   acq_utm_campaign   utm_campaign at landing
//   acq_platform       always 'web' for this collector (mobile sends ios/android)
//
// Everything here is best-effort and fail-open: a malformed / missing value
// resolves to null and is simply not appended. Nothing in this block may throw
// into the auth path.
// ---------------------------------------------------------------------------

/** The locked acquisition channel taxonomy. Kept in sync with the backend
 *  sanitizer allowlist and the PostHog custom channel types. Order below in
 *  `deriveAcqChannel` is first-match-wins. */
export type AcqChannel =
  | 'Direct'
  | 'Referral'
  | 'Organic Search'
  | 'Paid Search'
  | 'Social'
  | 'AI Assistant'
  | 'App Store'
  | 'Email'
  | 'Other';

/** Referring hosts that mean "an AI assistant sent this visitor" via a bare
 *  referral (no UTM). DERIVED (item 49) from the canonical `LLM_SOURCES`
 *  constant in `@repo/analytics` so the shared hosts can never drift, minus the
 *  ONE deliberate divergence: DuckDuckGo (AW-5). DDG is the largest privacy
 *  WEB-SEARCH engine, so a bare DDG referral is Organic Search (it lives in
 *  SEARCH_HOSTS below), NOT AI Assistant. DDG only routes to AI Assistant when
 *  it arrives with an explicit `ai-assistant` medium. (instrumentation-client
 *  still keeps DDG as an LLM `utm_source`, which is a distinct, explicit signal
 *  — not a referrer-host classification.) The subtraction below is the single
 *  point where this referrer-context carve-out lives. */
const AI_ASSISTANT_HOSTS: ReadonlySet<string> = new Set<string>(
  LLM_SOURCES.filter((h) => h !== 'duckduckgo.com'),
);

/** App-store referring hosts. */
const APP_STORE_HOSTS: ReadonlySet<string> = new Set<string>([
  'apps.apple.com',
  'play.google.com',
]);

/** Organic-search referring hosts (unpaid). Paid is disambiguated by utm. */
const SEARCH_HOSTS: ReadonlySet<string> = new Set<string>([
  'google.com',
  'www.google.com',
  'bing.com',
  'www.bing.com',
  'yahoo.com',
  'search.yahoo.com',
  'duckduckgo.com',
  'ecosia.org',
  'startpage.com',
  'baidu.com',
  'yandex.com',
  'yandex.ru',
]);

/** Social referring hosts (unpaid). */
const SOCIAL_HOSTS: ReadonlySet<string> = new Set<string>([
  'facebook.com',
  'www.facebook.com',
  'm.facebook.com',
  'l.facebook.com',
  'instagram.com',
  'www.instagram.com',
  'l.instagram.com',
  't.co',
  'twitter.com',
  'x.com',
  'linkedin.com',
  'www.linkedin.com',
  'lnkd.in',
  'reddit.com',
  'www.reddit.com',
  'old.reddit.com',
  'out.reddit.com',
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'tiktok.com',
  'www.tiktok.com',
  'pinterest.com',
  'threads.net',
]);

/** `utm_medium` values that indicate paid traffic. */
const PAID_MEDIUMS: ReadonlySet<string> = new Set<string>([
  'cpc',
  'ppc',
  'cpm',
  'cpv',
  'cpa',
  'paid',
  'retargeting',
]);

/** `utm_medium` values that indicate email. */
const EMAIL_MEDIUMS: ReadonlySet<string> = new Set<string>([
  'email',
  'e-mail',
  'newsletter',
]);

/** `utm_medium` values that indicate social. */
const SOCIAL_MEDIUMS: ReadonlySet<string> = new Set<string>([
  'social',
  'social-media',
  'social-network',
  'sm',
]);

/**
 * Reduce a host to a stable, cohortable form. Lower-cases and strips a single
 * leading generic subdomain prefix so mobile / link-shim variants collapse
 * onto their canonical host (AW-2): e.g. `m.youtube.com` -> `youtube.com`,
 * `l.facebook.com` -> `facebook.com`, `out.reddit.com` is left alone (only the
 * enumerated prefixes below are stripped). The regex is anchored to the exact
 * prefix TOKEN plus a dot so legitimate second-level hosts that merely start
 * with these letters (e.g. `medium.com`, `line.me`) are never over-stripped.
 *
 * MUST stay in sync with the same reduction in `instrumentation-client.ts`
 * (initial_referring_domain) so the first-touch super-prop and the OAuth-URL
 * `acq_referrer` agree on the same canonical host.
 */
const SUBDOMAIN_PREFIX_PATTERN = /^(www|m|mobile|l|lm|touch)\./;
const normalizeHost = (host: string): string =>
  host.toLowerCase().replace(SUBDOMAIN_PREFIX_PATTERN, '');

/**
 * Skim's own hosts. A `document.referrer` pointing at any of these is an
 * in-site navigation (a cross-page load into an auth surface, a redirect back
 * to /sign-in, etc.), NOT a genuine external referral. Left unhandled, such a
 * self-referral falls through `deriveAcqChannel` to the final `Referral`
 * clause and inflates Referral while polluting the Direct cohort (~85% of
 * signups). We coerce these to `$direct` so the channel derives to Direct and
 * no self-host reaches the wire as an external referrer (AW-1).
 *
 * Built from NEXT_PUBLIC_APP_URL's host plus the known prod + staging hosts,
 * each run through the same normalization as referrers. FAIL-OPEN: if the env
 * URL is unparseable we still seed the set with the literal prod host.
 */
const SELF_HOSTS: ReadonlySet<string> = (() => {
  const hosts = new Set<string>(['skim.plus', 'staging.skim.plus']);
  try {
    const appUrl = env.NEXT_PUBLIC_APP_URL;
    if (appUrl) {
      const envHost = new URL(appUrl).hostname;
      if (envHost) hosts.add(envHost);
    }
  } catch {
    // Unparseable NEXT_PUBLIC_APP_URL — fall back to the literal prod host,
    // which is already seeded above. Never throw at module-eval time.
    hosts.add('skim.plus');
  }
  // Normalize every seeded host so comparisons against a normalized referrer
  // host are apples-to-apples (strips www./m./staging stays as-is etc.).
  return new Set<string>(Array.from(hosts, (h) => normalizeHost(h)));
})();

/**
 * True when a normalized host is one of Skim's own (a self-referral). Any
 * `*.skim.plus` subdomain counts, not just the enumerated set: the
 * extension-driven signup flow lands with `extension.skim.plus` as
 * `document.referrer`, and `normalizeHost` only strips the enumerated generic
 * prefixes, so an exact-match Set alone misses it. Left uncaught, that
 * referrer falls through to the `Referral` channel and is persisted onto
 * write-once User acquisition columns. Coercing to `$direct` preserves the
 * AW-1 invariant; the came-via-extension signal survives via the `aa=`
 * attempt-id join.
 */
const isSelfHost = (host: string): boolean =>
  SELF_HOSTS.has(host) || host === 'skim.plus' || host.endsWith('.skim.plus');

/**
 * OAuth provider hosts (ATTR-5, 2026-07-05). On a retry / second auth click
 * the browser can still hold the provider's accounts page as
 * `document.referrer` (a bounce back from the Google/Apple consent screen),
 * which previously derived acq_channel=Referral /
 * acq_referrer=accounts.google.com. First-touch acquisition persists to
 * write-once User DB columns, so one bounced click corrupts that user's
 * attribution permanently. A provider bounce is a leg of OUR auth flow, not
 * an external referral — coerce to `$direct` exactly like self-hosts. Hosts
 * are compared post-`normalizeHost` (which leaves both of these untouched:
 * `accounts.` / `appleid.` are not in the stripped-prefix set).
 */
const OAUTH_PROVIDER_HOSTS: ReadonlySet<string> = new Set<string>([
  'accounts.google.com',
  'appleid.apple.com',
]);

/**
 * Parse `document.referrer` down to a bare host, or return `$direct` when
 * there is no referrer / it is unparseable. Returns `null` only on total
 * failure (SSR, thrown parse) so the caller can drop the param entirely.
 *
 * `$direct` is a first-class value here (not null) because ~85% of Skim
 * signups are direct and the backend allowlists `$direct` explicitly. An
 * empty referrer is real signal, not missing data.
 */
export const deriveReferringDomain = (): string | null => {
  if (typeof document === 'undefined') return null;
  try {
    const raw = document.referrer;
    if (!raw) return '$direct';
    const host = new URL(raw).hostname;
    if (!host) return '$direct';
    const normalized = normalizeHost(host);
    // Self-referral (in-site navigation into an auth surface, redirect back to
    // /sign-in, etc.) is NOT an external referral — coerce to `$direct` so the
    // channel derives to Direct and the own host is never emitted as an
    // external `acq_referrer` (AW-1). OAuth provider bounces are coerced the
    // same way (ATTR-5) — see OAUTH_PROVIDER_HOSTS above.
    if (isSelfHost(normalized) || OAUTH_PROVIDER_HOSTS.has(normalized)) {
      return '$direct';
    }
    return normalized;
  } catch {
    return null;
  }
};

/**
 * Read a UTM param from the current landing URL. Returns null when absent or
 * when `window` is unavailable (SSR). Best-effort; never throws.
 */
const readUtm = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};

/**
 * Coerce a caller-supplied surface into the backend's `SURFACE_PATTERN`
 * (`/^[a-z0-9_]{1,64}$/`) BEFORE it goes on the wire (AW-4). Without this, a
 * future caller passing a hyphenated label (the codebase already uses e.g.
 * 'mobile-top-row', 'rail-footer', 'mobile-drawer') would fail the backend
 * regex and get silently dropped to null — losing the STEP-1 canary. We
 * lower-case, replace any run of non-`[a-z0-9_]` with a single `_`, trim
 * leading/trailing `_`, and slice to 64. An empty / non-string result yields
 * null so nothing invalid is sent. Never throws.
 */
const normalizeSurface = (surface: string | null): string | null => {
  try {
    if (typeof surface !== 'string') return null;
    const cleaned = surface
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64);
    return cleaned.length > 0 ? cleaned : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Entry-touch acquisition store (Wave-4 item 37, UTM end-to-end fix).
//
// THE BREAK THIS CLOSES: before this store, `collectAcquisitionParams` read
// the UTM params from `window.location.search` AT CLICK TIME. A visitor who
// lands on `/?utm_source=test&utm_campaign=x`, soft-navigates (App Router SPA
// nav rewrites the URL, dropping the query), and THEN clicks a sign-in button
// reached the OAuth-init URL builder with an empty search string - the
// landing UTMs never went on the wire, and the write-once User acquisition
// columns persisted null. Same class of loss for the referrer on internal
// HARD navigations: `document.referrer` becomes skim.plus, which the
// self-host rule correctly coerces to `$direct` - the external first touch
// was gone.
//
// FIX: capture the acquisition inputs ONCE at analytics boot (the
// instrumentation-client runs on every full document load, before any click)
// into sessionStorage, and let `collectAcquisitionParams` prefer the stored
// entry touch over the click-time reads.
//
// Semantics ("entry touch", industry last-non-direct within the tab session):
//   - The FIRST document load in a tab session writes the store.
//   - Internal navigation (self-referrer, no UTM) NEVER overwrites it - this
//     is the case the store exists to survive.
//   - A NEW external/tagged entry (non-$direct external referrer OR any UTM
//     param on the landing URL) DOES overwrite: it is a genuinely new
//     acquisition touch for the visit that may convert.
//   - A bare direct re-entry never clobbers a known source.
// sessionStorage (not localStorage) keeps the value visit-scoped - the same
// scope `document.referrer` has - and copies into same-origin popups, so the
// /sign-in popup flow inherits the opener tab's entry touch. All fail-open.
// ---------------------------------------------------------------------------

const ACQ_FIRST_TOUCH_KEY = 'skim_acq_first_touch';

/** The entry-touch acquisition record persisted at analytics boot. */
export interface FirstTouchAcquisition {
  acq_referrer: string | null;
  acq_utm_source: string | null;
  acq_utm_medium: string | null;
  acq_utm_campaign: string | null;
  captured_at: number;
}

/** Coerce an unknown JSON field to a non-empty string or null. */
const asNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * Read the persisted entry-touch record. Returns null when absent, corrupt,
 * or when sessionStorage is unavailable - callers fall back to the live
 * click-time reads (the pre-store behavior). Never throws.
 */
export const readFirstTouchAcquisition = (): FirstTouchAcquisition | null => {
  const ss = safeSessionStorage();
  if (!ss) return null;
  try {
    const raw = ss.getItem(ACQ_FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FirstTouchAcquisition>;
    if (typeof parsed.captured_at !== 'number') return null;
    return {
      acq_referrer: asNonEmptyString(parsed.acq_referrer),
      acq_utm_source: asNonEmptyString(parsed.acq_utm_source),
      acq_utm_medium: asNonEmptyString(parsed.acq_utm_medium),
      acq_utm_campaign: asNonEmptyString(parsed.acq_utm_campaign),
      captured_at: parsed.captured_at,
    };
  } catch {
    return null;
  }
};

/**
 * Capture the current document load's acquisition inputs into the
 * entry-touch store. Called from `instrumentation-client.ts` at analytics
 * boot (once per full document load, before any user interaction).
 *
 * Overwrite policy (see the block comment above): write when the store is
 * empty OR when this load is itself a new external/tagged entry (non-$direct
 * external referrer or any UTM present). Keep the store on internal
 * navigations and bare direct re-entries. Returns the effective record (the
 * kept or newly written one), or null when storage is unavailable.
 */
export const captureFirstTouchAcquisition =
  (): FirstTouchAcquisition | null => {
    const ss = safeSessionStorage();
    if (!ss) return null;
    try {
      const referrer = deriveReferringDomain();
      const utmSource = readUtm('utm_source');
      const utmMedium = readUtm('utm_medium');
      const utmCampaign = readUtm('utm_campaign');

      const isExternalOrTaggedEntry =
        (referrer !== null && referrer !== '$direct') ||
        utmSource !== null ||
        utmMedium !== null ||
        utmCampaign !== null;

      const existing = readFirstTouchAcquisition();
      if (existing && !isExternalOrTaggedEntry) return existing;

      const record: FirstTouchAcquisition = {
        acq_referrer: referrer,
        acq_utm_source: utmSource,
        acq_utm_medium: utmMedium,
        acq_utm_campaign: utmCampaign,
        captured_at: Date.now(),
      };
      ss.setItem(ACQ_FIRST_TOUCH_KEY, JSON.stringify(record));
      return record;
    } catch {
      return null;
    }
  };

/** The acquisition inputs derived from referrer + landing UTM at click time. */
export interface AcquisitionParams {
  surface: string | null;
  acq_channel: AcqChannel | null;
  acq_referrer: string | null;
  acq_utm_source: string | null;
  acq_utm_medium: string | null;
  acq_utm_campaign: string | null;
  acq_platform: string | null;
}

/**
 * Derive the acquisition channel from a bare referring host + UTM values.
 * First-match-wins, paid before organic, Direct before Referral. Mirrors the
 * locked taxonomy (Direct / Referral / Organic Search / Paid Search / Social /
 * AI Assistant / App Store / Email / Other). Pure + total, never throws.
 *
 * `$direct` (no referrer) with no UTM medium maps to Direct. A present,
 * non-`$direct` host that matches no known bucket maps to Referral.
 */
export const deriveAcqChannel = (
  referringDomain: string | null,
  utmSource: string | null,
  utmMedium: string | null,
): AcqChannel | null => {
  try {
    const host = referringDomain ? referringDomain.toLowerCase() : null;
    const source = utmSource ? utmSource.toLowerCase() : '';
    const medium = utmMedium ? utmMedium.toLowerCase() : '';
    const isPaid = PAID_MEDIUMS.has(medium) || medium.startsWith('paid');

    // Email first: an email link can carry a search-engine referrer.
    if (EMAIL_MEDIUMS.has(medium) || EMAIL_MEDIUMS.has(source)) {
      return 'Email';
    }

    // Paid search (paid medium + a search source/host).
    if (
      isPaid &&
      (SEARCH_HOSTS.has(host ?? '') ||
        ['google', 'bing', 'yahoo', 'duckduckgo'].includes(source))
    ) {
      return 'Paid Search';
    }

    // AI assistant: either an explicit medium or a known AI host.
    if (
      medium === 'ai-assistant' ||
      (host !== null && AI_ASSISTANT_HOSTS.has(host))
    ) {
      return 'AI Assistant';
    }

    // App store referral.
    if (
      (host !== null && APP_STORE_HOSTS.has(host)) ||
      source === 'apps.apple.com' ||
      source === 'play.google.com'
    ) {
      return 'App Store';
    }

    // Direct: no referrer (`$direct`) AND no marketing medium.
    if ((host === null || host === '$direct') && !medium && !source) {
      return 'Direct';
    }

    // Organic search: unpaid, search host or organic medium.
    if (
      medium === 'organic' ||
      (host !== null && SEARCH_HOSTS.has(host)) ||
      ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia'].includes(source)
    ) {
      return 'Organic Search';
    }

    // Social: social host or social medium.
    if (
      SOCIAL_MEDIUMS.has(medium) ||
      (host !== null && SOCIAL_HOSTS.has(host))
    ) {
      return 'Social';
    }

    // Any present, non-direct referrer that matched nothing above is a Referral.
    if (host !== null && host !== '$direct') {
      return 'Referral';
    }

    // A UTM-only landing (marketing link, no recognized bucket) is Other.
    if (medium || source) {
      return 'Other';
    }

    // Fall back to Direct: an empty referrer with no UTM is a direct visit.
    return 'Direct';
  } catch {
    return null;
  }
};

/**
 * Collect the acquisition params for the current click. `surface` is passed
 * in by the caller (it mirrors however surface is already computed for the
 * in-app auth events; the popup passes the attempt's surface). Never throws;
 * any failure yields all-null so nothing gets appended.
 *
 * Source precedence (item 37): the entry-touch store written at analytics
 * boot is authoritative when present - INCLUDING its nulls (a clean direct
 * landing IS the visit's acquisition truth; a mid-session URL that happens
 * to carry utm_* must not overwrite it). Only when the store is unavailable
 * (storage blocked, boot never ran - e.g. unit contexts) do we fall back to
 * the live click-time reads, which is exactly the pre-store behavior.
 */
export const collectAcquisitionParams = (
  surface: string | null,
): AcquisitionParams => {
  try {
    const firstTouch = readFirstTouchAcquisition();
    const referrer = firstTouch
      ? firstTouch.acq_referrer
      : deriveReferringDomain();
    const utmSource = firstTouch
      ? firstTouch.acq_utm_source
      : readUtm('utm_source');
    const utmMedium = firstTouch
      ? firstTouch.acq_utm_medium
      : readUtm('utm_medium');
    const utmCampaign = firstTouch
      ? firstTouch.acq_utm_campaign
      : readUtm('utm_campaign');
    return {
      // Normalize surface to the backend SURFACE_PATTERN before it goes on the
      // wire (AW-4) so a hyphenated / dirty caller label can never be silently
      // dropped to null by the receiver. Do NOT drop 'unknown': a non-null
      // surface on the wire is precisely the STEP-1 canary signal.
      surface: normalizeSurface(surface),
      acq_channel: deriveAcqChannel(referrer, utmSource, utmMedium),
      acq_referrer: referrer,
      acq_utm_source: utmSource,
      acq_utm_medium: utmMedium,
      acq_utm_campaign: utmCampaign,
      // The website is always acquired on the `web` platform. Mobile clients
      // send `ios` / `android` from their own collector; this collector is
      // web-only, so the value is a constant.
      acq_platform: 'web',
    };
  } catch {
    return {
      surface: null,
      acq_channel: null,
      acq_referrer: null,
      acq_utm_source: null,
      acq_utm_medium: null,
      acq_utm_campaign: null,
      // Even on the fail-open path the platform is unambiguously web.
      acq_platform: 'web',
    };
  }
};

/**
 * Append acquisition params to an OAuth-init URL, mirroring exactly how
 * `appendAuthAttemptId` threads `aa=` (same `?` vs `&` handling, same
 * `encodeURIComponent`). PURELY ADDITIVE: it never touches `redirect_to` or
 * `aa`; it only tacks on the snake_case acquisition keys the backend receiver
 * reads. Any null value is skipped. FAIL-OPEN: on any throw it returns the
 * original url unchanged so the auth click is never blocked or delayed.
 *
 * KEYS (must match the backend `req.query` / OAuth-state contract exactly):
 *   surface, acq_channel, acq_referrer,
 *   acq_utm_source, acq_utm_medium, acq_utm_campaign, acq_platform
 */
export const appendAcquisitionParams = (
  url: string,
  params: AcquisitionParams,
): string => {
  try {
    const pairs: Array<[string, string | null]> = [
      ['surface', params.surface],
      ['acq_channel', params.acq_channel],
      ['acq_referrer', params.acq_referrer],
      ['acq_utm_source', params.acq_utm_source],
      ['acq_utm_medium', params.acq_utm_medium],
      ['acq_utm_campaign', params.acq_utm_campaign],
      ['acq_platform', params.acq_platform],
    ];
    let out = url;
    for (const [key, value] of pairs) {
      if (value === null || value === undefined || value === '') continue;
      const sep = out.includes('?') ? '&' : '?';
      out = `${out}${sep}${key}=${encodeURIComponent(value)}`;
    }
    return out;
  } catch {
    // Fail-open: acquisition plumbing must never block the auth path.
    return url;
  }
};

/**
 * Build the first-touch acquisition person props for PostHog `$set_once`
 * (Wave-4 item 38). Reads the SAME merged values the OAuth wire sends (the
 * entry-touch store, falling back to live reads), so the PostHog person and
 * the server-persisted write-once User acquisition columns agree on one
 * first-touch story. Keys carry the `_first` suffix; null values are DROPPED
 * (never $set_once a null - it would permanently occupy the write-once slot
 * with nothing). `$direct` is a first-class referrer value and IS included.
 *
 * Caller contract (the identity bridge): only call this AFTER `safeIdentify`
 * returned true - a team-guard-rejected / sentinel id must never receive
 * person props. Returns null when nothing usable was derived. Never throws.
 */
export const buildFirstTouchSetOnceProps = (): Record<
  string,
  string
> | null => {
  try {
    const acq = collectAcquisitionParams(null);
    const props: Record<string, string> = {};
    if (acq.acq_channel) props.acq_channel_first = acq.acq_channel;
    if (acq.acq_referrer) props.acq_referrer_first = acq.acq_referrer;
    if (acq.acq_utm_source) props.acq_utm_source_first = acq.acq_utm_source;
    if (acq.acq_utm_medium) props.acq_utm_medium_first = acq.acq_utm_medium;
    if (acq.acq_utm_campaign)
      props.acq_utm_campaign_first = acq.acq_utm_campaign;
    if (acq.acq_platform) props.acq_platform_first = acq.acq_platform;
    return Object.keys(props).length > 0 ? props : null;
  } catch {
    return null;
  }
};

/**
 * Common event payload — ms_since_initiated derived from t0 at fire time,
 * not from the storage timestamp (handles cases where stage updates lag
 * behind the wall clock).
 */
export const buildEventBase = (
  attempt: AuthAttemptRecord | null,
): Record<string, unknown> => {
  if (!attempt) return {};
  return {
    auth_attempt_id: attempt.id,
    provider: attempt.provider,
    surface: attempt.surface,
    flow: attempt.flow,
    ms_since_initiated: Math.max(0, Date.now() - attempt.t0),
  };
};

// ---------------------------------------------------------------------------
// Watchdog (event 10: auth_flow_abandoned)
// ---------------------------------------------------------------------------

/**
 * Stages from which abandonment can fire. If we're already in a terminal
 * state (synced, sync_failed, abandoned, closed_no_result + handled), the
 * watchdog should NOT re-fire — those are accounted for by their own
 * events. We treat closed_no_result as non-terminal because it can still
 * progress to synced via the rescue refetch.
 *
 * 'result_posted' is deliberately NOT abandonable (AUTH-2, 2026-07-05): in
 * the redirect flow there is no parent listener to advance the stage past
 * it, so every record parked there is overwhelmingly a COMPLETED sign-in —
 * 2/2 auth_flow_abandoned since launch were successful sign-ins. The
 * callback page now clears the record on success (belt), and this exclusion
 * is the braces for any success path that misses the clear. A genuine
 * failure at that stage is already visible via
 * auth_session_established_client ok:false / auth_result_posted
 * success:false, so removing it costs no real signal.
 */
const ABANDONABLE_STAGES: ReadonlySet<AuthStage> = new Set<AuthStage>([
  'initiated',
  'callback_landed',
  'result_received',
  'closed_no_result',
]);

/**
 * Single-tick `evaluate()` for the watchdog — separated from
 * `installAuthAbandonWatchdog` so the watchdog component can call it on
 * pathname changes (Next.js soft-navigations don't trigger
 * visibilitychange, so the layout-mounted watchdog alone misses them).
 */
export const evaluateAuthAbandon = (
  capture: (event: string, props?: Record<string, unknown>) => void,
): void => {
  if (typeof window === 'undefined') return;
  const attempt = getCurrentAuthAttempt();
  if (!attempt) return;
  if (!ABANDONABLE_STAGES.has(attempt.stage)) return;
  if (Date.now() - attempt.t0 < ABANDON_THRESHOLD_MS) return;
  try {
    capture('auth_flow_abandoned', {
      ...buildEventBase(attempt),
      last_stage: attempt.stage,
    });
  } catch {
    // best-effort
  }
  clearAuthAttempt();
};

/**
 * Install a one-shot watchdog that fires `auth_flow_abandoned` on the next
 * pageview/interaction if a stale (>90s) non-terminal attempt is sitting in
 * sessionStorage. Mounted from a root client component (`AuthAbandonWatchdog`)
 * so it runs on every page load. Listens to:
 *   1. mount — covers the "user came back to a stale tab" case
 *   2. visibilitychange → visible — covers tab refocus without navigation
 *
 * For soft-navigation re-evaluation (the in-SPA route change case), the
 * watchdog component subscribes to `usePathname()` and calls
 * `evaluateAuthAbandon` directly — this installer doesn't try to do that
 * because hooking router events from a vanilla function isn't reliable
 * across the App Router's render lifecycle.
 *
 * Returns a teardown function. Designed for `useEffect` consumption.
 */
export const installAuthAbandonWatchdog = (
  capture: (event: string, props?: Record<string, unknown>) => void,
): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  evaluateAuthAbandon(capture);
  const onVisibility = () => {
    if (document.visibilityState === 'visible') evaluateAuthAbandon(capture);
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
  };
};
